import { supabase } from '../../lib/supabase';

/**
 * Handles file storage operations for recordings
 */
export class StorageService {
  // Maximum number of retries for downloading files
  private MAX_RETRIES = 3;
  // Delay between retries in milliseconds
  private RETRY_DELAY = 1000;

  /**
   * Upload a recording blob to storage
   */
  async uploadRecording(userId: string, blob: Blob, fileName: string): Promise<string> {
    try {
      // Check if fileName already contains userId to avoid duplication
      // Extract just the filename without any path
      const baseFileName = fileName.split('/').pop() || fileName;
      const filePath = `${userId}/${baseFileName}`;
      console.log('Uploading recording:', filePath);
      
      const { error: uploadError } = await supabase.storage
        .from('recordings')
        .upload(filePath, blob);

      if (uploadError) {
        console.error('Upload error:', uploadError);
        throw new Error('Failed to save your recording. Please try again.');
      }

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('recordings')
        .getPublicUrl(filePath);
      
      console.log('Recording URL:', publicUrl);
      return publicUrl;
    } catch (error) {
      console.error('Error uploading recording:', error);
      throw error;
    }
  }

  /**
   * Delete a recording from storage
   */
  async deleteRecordingFile(url: string): Promise<void> {
    try {
      // Extract the file path from the recording URL
      const urlObj = new URL(url);
      const pathParts = urlObj.pathname.split('/');
      // Remove 'storage/v1/object/public/recordings/' from the path
      const filePath = pathParts.slice(5).join('/');

      console.log('Deleting recording file:', filePath);
      
      // Delete the file from storage
      const { error: storageError } = await supabase.storage
        .from('recordings')
        .remove([filePath]);

      if (storageError) {
        console.error('Error deleting storage file:', storageError);
      }
    } catch (error) {
      console.error('Error deleting recording file:', error);
      throw error;
    }
  }

  /**
   * Verify a file is accessible by URL
   */
  async verifyFileAccess(url: string, retries = 0): Promise<boolean> {
    try {
      // Try to get the file headers
      console.log('Verifying access to URL:', url);
      const response = await fetch(url, { method: 'HEAD' });
      
      console.log('File access check response:', {
        status: response.status,
        ok: response.ok,
        contentType: response.headers.get('content-type'),
        contentLength: response.headers.get('content-length')
      });
      
      if (response.ok) {
        return true;
      }

      if (response.status === 502 && retries < this.MAX_RETRIES) {
        console.log(`Retry ${retries + 1} for URL: ${url}`);
        await new Promise(resolve => setTimeout(resolve, this.RETRY_DELAY));
        return this.verifyFileAccess(url, retries + 1);
      }

      return false;
    } catch (error) {
      console.error('Error verifying file access:', error);
      if (retries < this.MAX_RETRIES) {
        await new Promise(resolve => setTimeout(resolve, this.RETRY_DELAY));
        return this.verifyFileAccess(url, retries + 1);
      }
      return false;
    }
  }

  /**
   * Fetch a file with retry mechanism
   */
  async fetchWithRetry(url: string, options: RequestInit = {}, retries = 0): Promise<Response> {
    try {
      console.log('Fetching file from URL:', url);
      const response = await fetch(url, options);
      
      console.log('Fetch response:', {
        status: response.status,
        ok: response.ok,
        contentType: response.headers.get('content-type'),
        contentLength: response.headers.get('content-length')
      });
      
      if (response.ok) {
        return response;
      }

      if (response.status === 502 && retries < this.MAX_RETRIES) {
        console.log(`Retry ${retries + 1} for URL: ${url}`);
        await new Promise(resolve => setTimeout(resolve, this.RETRY_DELAY));
        return this.fetchWithRetry(url, options, retries + 1);
      }

      throw new Error(`Failed to fetch file: ${response.status} ${response.statusText}`);
    } catch (error) {
      if (retries < this.MAX_RETRIES) {
        await new Promise(resolve => setTimeout(resolve, this.RETRY_DELAY));
        return this.fetchWithRetry(url, options, retries + 1);
      }
      throw error;
    }
  }
}